import React, { useCallback, useEffect, useState, useMemo } from "react";
import Header from "../Header";
import Sidebar from "./Sidebar";
import {
  Box,
  IconButton,
  Typography,
  Button,
  Backdrop,
  Modal,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Tooltip,
  Chip,
} from "@mui/material";
import theme from "../../theme";
import { makeStyles } from "@mui/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import { PromptModal } from "./PromptModal";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid } from "@mui/x-data-grid";
import { BASE_URL } from "../../Constants/Constants";
import axios from "axios";
import { getToken } from "../../utils/utils";
import { toast, ToastContainer } from "react-toastify";
import CustomLoader from "../../utils/CustomLoader";
import {
  FETCH_ERROR,
  PROMPT_FIELD_REQUIRED_MESSAGE,
  PROMPT_CREATE_SUCCESS_MESSAGE,
  PROMPT_DELETE_SUCCESS_MESSAGE,
  PROMPT_EDIT_SUCCESS_MESSAGE,
  STATUS_SELECTION_ERROR_MESSAGE,
  TYPE_SELECTION_ERROR_MESSAGE,
} from "../../utils/Validation";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import RestoreIcon from "@mui/icons-material/Restore";
import { PROMPT_TYPES, FILE_CATEGORY } from "../../Constants/Constants";
import { useTranslation } from "react-i18next";
import FilterListIcon from "@mui/icons-material/FilterList";

const useStyles = makeStyles((themes) => ({
  MainContainer: {
    display: "flex",
    width: "100%",
    background: "#F1F1F1",
  },
  ContentContainer: {
    width: "80%",
    padding: 20,
    height: "93vh",
    display: "flex",
    flexDirection: "column",
    background: "#fff",
  },
  Breadcrumbs: {
    display: "flex",
    gap: 4,
    alignItems: "center",
    marginBottom: "20px",
    justifyContent: "space-between",
  },
  BreadcrumbsText: {
    color: theme.palette.secondary.black,
    fontFamily: "Helvetica",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "30px",
  },
  BreadcrumbsIcon: {
    width: "24px",
    height: "24px",
  },
  tableContainer: {
    width: "100%",
  },
  tableHeaderCell: {
    fontWeight: "bold",
    backgroundColor: theme.palette.secondary.main,
    color: "#000",
  },
  BatchActionButton: {
    backgroundColor: "#F3F7F9 !important",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "180px",
    height: "30px",
    padding: "7px 8.75px",
    gap: "10px",
    borderRadius: "3px 0px 0px 0px",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    opacity: "0.65",
    marginBottom: "30px !important",
    textTransform: "none",
  },
  secondaryText: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    color: theme.palette.secondary.black,
  },
  gridContainer: {
    padding: "20px",
  },
  ButtonContainer: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  CreateIcon: {
    height: "40px",
  },
  CreatePromptText: {
    textTransform: "capitalize",
  },
  Modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 800,
    backgroundColor: "#fff",
    minHeight: 350,
    boxShadow: 24,
    padding: 30,
    borderRadius: 10,
  },
  TextInput: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  Btn: {
    width: 200,
  },
}));
// const label = { inputProps: { "aria-label": "Disable Switch" } };

const Prompts = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  // Helper functions first - before any variables that depend on them
  // Function to get the display name of a prompt based on its type value
  const getPromptTypeKey = (typeValue) => {
    const matchedEntry = Object.entries(PROMPT_TYPES).find(
      ([, value]) => value === typeValue
    );
    return matchedEntry ? matchedEntry[0] : "Unknown"; // Return the key if found, else "Unknown"
  };

  // Function to get the category of a prompt based on its type
  const getPromptCategory = (typeValue) => {
    const promptKey = getPromptTypeKey(typeValue);

    if (promptKey.includes("Distribution and Leasing Services")) {
      return FILE_CATEGORY.distribution;
    } else if (promptKey.includes("IT Services and Provision")) {
      return FILE_CATEGORY.it;
    } else if (promptKey.includes("General Tender")) {
      return FILE_CATEGORY.general;
    } else {
      return "General";
    }
  };

  // Function to get prompt type (Analysis, Comments, Technical Comments, or General)
  const getPromptSection = (typeValue) => {
    const promptKey = getPromptTypeKey(typeValue);

    if (promptKey.includes("Analysis")) {
      return "Analysis";
    } else if (
      promptKey.includes("Comments") &&
      !promptKey.includes("Technical Comments")
    ) {
      return "Comments";
    } else if (promptKey.includes("Technical Comments")) {
      return "Technical Comments";
    } else {
      return "General";
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [showModalOpen, setShowModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [PromptsAPIData, setPromptsAPIData] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [createPrompt, setCreatePrompt] = useState({
    id: null,
    type: "",
    prompt: "",
    is_active: "Active",
  });
  const [isLoading, setIsLoading] = useState(false);

  // Filter state
  const [filterOpen, setFilterOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Transform the API data into rows with computed properties
  const rows = useMemo(() => {
    if (!PromptsAPIData) return [];

    return PromptsAPIData.map((item) => ({
      id: item.id,
      type: item.type,
      promptType: getPromptTypeKey(item.type),
      category: getPromptCategory(item.type),
      section: getPromptSection(item.type),
      default_prompt: item.default_prompt,
      prompt: item.prompt,
      isActive: item.is_active ? "Active" : "Inactive",
    }));
  }, [PromptsAPIData, getPromptTypeKey, getPromptCategory, getPromptSection]);

  // Apply filters to rows
  const filteredRows = useMemo(() => {
    if (!rows.length) return [];

    return rows.filter((row) => {
      const matchesCategory =
        !categoryFilter || row.category === categoryFilter;
      const matchesSection = !sectionFilter || row.section === sectionFilter;
      const matchesStatus = !statusFilter || row.isActive === statusFilter;

      return matchesCategory && matchesSection && matchesStatus;
    });
  }, [rows, categoryFilter, sectionFilter, statusFilter]);

  const handleFilterToggle = () => {
    setFilterOpen(!filterOpen);
  };

  const clearFilters = () => {
    setCategoryFilter("");
    setSectionFilter("");
    setStatusFilter("");
  };

  const handleModalClose = () => {
    setSelectedPrompt("");
    setShowModalOpen(false);
    setCreatePrompt({
      id: null,
      type: "",
      prompt: "",
      is_active: null,
    });
    setModalOpen(false);
    setIsEdit(false);
  };
  const handleModalOpen = () => {
    setShowModalOpen(true);
  };
  const token = getToken();
  const getData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${BASE_URL}/prompt/all/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setPromptsAPIData(response.data);
    } catch (error) {
      toast.error(FETCH_ERROR);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    getData();
  }, [token, getData]);

  const handleCreatePromptsChange = (e) => {
    const { name, value } = e.target;

    setCreatePrompt({ ...createPrompt, [name]: value });
  };
  const handlePromptCreateClick = async () => {
    if (!createPrompt.type) {
      toast.error(TYPE_SELECTION_ERROR_MESSAGE);
      return;
    }
    if (!createPrompt.prompt) {
      toast.error(PROMPT_FIELD_REQUIRED_MESSAGE);
      return;
    }

    if (createPrompt.is_active === null) {
      toast.error(STATUS_SELECTION_ERROR_MESSAGE);
      return;
    }

    const updatedPrompt = {
      ...createPrompt,
      is_active: createPrompt.is_active === "Active" ? true : false,
    };
    setCreatePrompt(updatedPrompt);

    try {
      setIsLoading(true);
      await axios.post(`${BASE_URL}/prompt/create/`, updatedPrompt, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success(PROMPT_CREATE_SUCCESS_MESSAGE);
      getData();
      setCreatePrompt({
        id: null,
        type: "",
        prompt: "",
        is_active: null,
      });
    } catch (error) {
      toast.error(FETCH_ERROR);
    } finally {
      setIsLoading(false);
      setModalOpen(false);
    }
  };
  const handlePromptEdit = async (selectedRow) => {
    setCreatePrompt({
      id: selectedRow.id,
      type: selectedRow.type,
      prompt: selectedRow.prompt,
      is_active: selectedRow.isActive === "Active" ? true : false,
    });
    setIsEdit(true);
    setModalOpen(true);
  };
  const handlePromptEditClick = async () => {
    if (!createPrompt.type) {
      toast.error(TYPE_SELECTION_ERROR_MESSAGE);
      return;
    }
    if (!createPrompt.prompt) {
      toast.error(PROMPT_FIELD_REQUIRED_MESSAGE);
      return;
    }
    try {
      setIsLoading(true);
      await axios.put(
        `${BASE_URL}/prompt/update/${createPrompt.id}/`,
        createPrompt,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(PROMPT_EDIT_SUCCESS_MESSAGE);
      getData();
      setCreatePrompt({
        id: null,
        type: "",
        prompt: "",
        is_active: null,
      });
    } catch (error) {
      toast.error(FETCH_ERROR);
    } finally {
      setIsLoading(false);
      setModalOpen(false);
      setIsEdit(false);
    }
  };
  const handlePromptsDelete = async (id) => {
    try {
      setIsLoading(true);
      await axios.delete(`${BASE_URL}/prompt/delete/${id}/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success(PROMPT_DELETE_SUCCESS_MESSAGE);
      getData();
    } catch (error) {
      toast.error(FETCH_ERROR);
    } finally {
      setIsLoading(false);
    }
  };
  const handleActivatePrompt = async (row) => {
    try {
      setIsLoading(true);

      // Create a payload with the correct data structure
      const payload = {
        id: row.id,
        prompt: row.prompt,
        type: row.type,
        is_active: true,
      };

      // First, make the API call
      await axios.put(`${BASE_URL}/prompt/update/${row.id}/`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      // Then, force refresh the data by making a new API call
      const response = await axios.get(`${BASE_URL}/prompt/all/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      // Update the state with fresh data
      setPromptsAPIData(response.data);

      toast.success("Prompt activated successfully!");
    } catch (error) {
      toast.error(FETCH_ERROR);
    } finally {
      setIsLoading(false);
    }
  };
  const handleResetToDefault = async (row) => {
    try {
      setIsLoading(true);

      // Create a payload with the correct data structure - no JSON.stringify needed
      const payload = {
        id: row.id,
        prompt: row.default_prompt,
        type: row.type,
        is_active: true,
      };

      await axios.put(`${BASE_URL}/prompt/update/${row.id}/`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Prompt reset to default successfully!");
      getData();
    } catch (error) {
      toast.error(FETCH_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      field: "id",
      headerName: t("tables.id"),
      width: 70,
      headerClassName: "tableHeaderCell",
    },
    {
      field: "category",
      headerName: t("tables.category"),
      width: 180,
      headerClassName: "tableHeaderCell",
      renderCell: (params) => {
        let color;
        switch (params.value) {
          case FILE_CATEGORY.distribution:
            color = "primary";
            break;
          case FILE_CATEGORY.it:
            color = "success";
            break;
          case FILE_CATEGORY.general:
            color = "warning";
            break;
          default:
            color = "default";
        }

        return (
          <Chip
            label={params.value}
            color={color}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: "section",
      headerName: t("tables.section"),
      width: 150,
      headerClassName: "tableHeaderCell",
      renderCell: (params) => {
        let color;
        switch (params.value) {
          case "Analysis":
            color = "info";
            break;
          case "Comments":
            color = "success";
            break;
          case "Technical Comments":
            color = "warning";
            break;
          default:
            color = "default";
        }

        return <Chip label={params.value} color={color} size="small" />;
      },
    },
    {
      field: "promptType",
      headerName: t("tables.type"),
      flex: 1,
      minWidth: 250,
      headerClassName: "tableHeaderCell",
      renderCell: (params) => (
        <span style={{ textTransform: "capitalize" }}>{params.value}</span>
      ),
    },
    {
      field: "prompt",
      headerName: t("tables.prompts"),
      flex: 1,
      minWidth: 200,
      headerClassName: "tableHeaderCell",
    },
    {
      field: "isActive",
      headerName: t("tables.status"),
      width: 100,
      headerClassName: "tableHeaderCell",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Chip
            label={params.value}
            color={params.value === "Active" ? "success" : "error"}
            size="small"
          />
        );
      },
    },
    {
      field: "actions",
      headerName: t("tables.actions"),
      width: 300,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Tooltip title={t("buttons.show")}>
            <IconButton
              color="info"
              aria-label="Show"
              onClick={() => {
                setSelectedPrompt(params.row);
                handleModalOpen();
              }}
            >
              <RemoveRedEyeIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("buttons.edit")}>
            <IconButton
              color="inherit"
              aria-label="edit"
              onClick={() => handlePromptEdit(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("buttons.delete")}>
            <IconButton
              color="primary"
              aria-label="delete"
              onClick={() => handlePromptsDelete(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset to Default">
            <IconButton
              color="warning"
              aria-label="reset to default"
              onClick={() => handleResetToDefault(params.row)}
            >
              <RestoreIcon />
            </IconButton>
          </Tooltip>
          {params.row.isActive === "Active" ? (
            <Tooltip title={t("buttons.activated")}>
              <span
                style={{ color: "green", fontWeight: "bold", padding: "8px" }}
              >
                {t("buttons.activated")}
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={t("buttons.activate")}>
              <Button
                variant="contained"
                style={{ backgroundColor: "#C91618", width: "100px" }}
                aria-label="activate"
                onClick={() => handleActivatePrompt(params.row)}
                size="small"
              >
                {t("buttons.activate")}
              </Button>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <Header />
      <Box className={classes.MainContainer}>
        <Sidebar />
        <Box className={classes.ContentContainer}>
          <Box className={classes.Breadcrumbs}>
            <Typography className={classes.BreadcrumbsText}>
              {t("prompts.listTitle")}
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={handleFilterToggle}
                color="inherit"
              >
                Filters
              </Button>
              <Button
                variant="contained"
                className={classes.CreateIcon}
                startIcon={<AddIcon />}
                onClick={() => setModalOpen(true)}
              >
                <Typography className={classes.CreatePromptText}>
                  {t("prompts.createPrompt")}
                </Typography>
              </Button>
            </Box>
          </Box>

          {filterOpen && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                p: 2,
                mb: 2,
                bgcolor: "#f5f5f5",
                borderRadius: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={FILE_CATEGORY.distribution}>
                    {FILE_CATEGORY.distribution}
                  </MenuItem>
                  <MenuItem value={FILE_CATEGORY.it}>
                    {FILE_CATEGORY.it}
                  </MenuItem>
                  <MenuItem value={FILE_CATEGORY.general}>
                    {FILE_CATEGORY.general}
                  </MenuItem>
                  <MenuItem value="General">General</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Section</InputLabel>
                <Select
                  value={sectionFilter}
                  onChange={(e) => setSectionFilter(e.target.value)}
                  label="Section"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Analysis">Analysis</MenuItem>
                  <MenuItem value="Comments">Comments</MenuItem>
                  <MenuItem value="Technical Comments">
                    Technical Comments
                  </MenuItem>
                  <MenuItem value="General">General</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>

              <Button variant="outlined" size="small" onClick={clearFilters}>
                Clear Filters
              </Button>
            </Box>
          )}

          <Box className={classes.tableContainer}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              autoHeight
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
              }}
            />
          </Box>
        </Box>
        <Backdrop className={classes.backdrop} open={isLoading}>
          <CustomLoader loading={isLoading} size={50} />
        </Backdrop>
        <ToastContainer />

        <PromptModal
          open={showModalOpen}
          handleClose={handleModalClose}
          promptData={selectedPrompt}
        />
        <Modal
          open={modalOpen}
          onClose={handleModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box className={classes.Modal}>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              {isEdit ? "Edit Prompts:" : "Create Prompts:"}
            </Typography>
            <Box className={classes.TextInput}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={createPrompt.type}
                  onChange={handleCreatePromptsChange}
                  label="Type"
                >
                  {Object.entries(PROMPT_TYPES).map(([key, value]) => (
                    <MenuItem key={key} value={value}>
                      {key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                variant="outlined"
                fullWidth
                multiline
                rows={15}
                sx={{ my: 2 }}
                placeholder="Prompts"
                label="Prompts"
                name="prompt"
                value={createPrompt.prompt}
                onChange={handleCreatePromptsChange}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={createPrompt.is_active}
                  name="is_active"
                  onChange={handleCreatePromptsChange}
                  label={"Status"}
                >
                  {["Active", "Inactive"].map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isEdit ? (
                <Button
                  variant="outlined"
                  className={classes.Btn}
                  onClick={handlePromptEditClick}
                >
                  Edit
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  className={classes.Btn}
                  onClick={handlePromptCreateClick}
                >
                  Create Prompts
                </Button>
              )}
            </Box>
          </Box>
        </Modal>
      </Box>
    </>
  );
};

export default Prompts;
